import { apiFetch } from './client'
import type { components } from '@helmetcare/api-client'

export interface LoginResponse {
  success: boolean
  data: components['schemas']['AuthLoginSuccess'] | components['schemas']['AuthMFARequired']
}

export interface MfaVerifyResponse {
  success: boolean
  data: components['schemas']['AuthTokenResponse']
}

export interface RefreshResponse {
  success: boolean
  data: components['schemas']['AuthTokenResponse']
}

export async function login(email: string, password: string): Promise<LoginResponse['data']> {
  return apiFetch<LoginResponse['data']>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
    skipAuth: true,
  })
}

export async function verifyMfa(
  sessionId: string,
  totpCode: string,
): Promise<components['schemas']['AuthTokenResponse']> {
  return apiFetch<components['schemas']['AuthTokenResponse']>('/auth/mfa/verify', {
    method: 'POST',
    body: JSON.stringify({ sessionId, totpCode }),
    skipAuth: true,
  })
}

export async function refresh(
  refreshToken: string,
): Promise<components['schemas']['AuthTokenResponse']> {
  return apiFetch<components['schemas']['AuthTokenResponse']>('/auth/refresh', {
    method: 'POST',
    body: JSON.stringify({ refreshToken }),
    skipAuth: true,
  })
}

export async function logout(): Promise<{ success: boolean }> {
  return apiFetch<{ success: boolean }>('/auth/logout', {
    method: 'POST',
  })
}

/** Safe identity fields returned for the authenticated user. */
export interface MyAccount {
  id: string
  email: string
  status: string
}

export interface ChangePasswordRequest {
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

export function getMe(): Promise<MyAccount> {
  return apiFetch<MyAccount>('/auth/me')
}

export function changePassword(body: ChangePasswordRequest): Promise<{ reauthenticate: true }> {
  return apiFetch('/auth/change-password', {
    method: 'POST',
    body: JSON.stringify(body),
    // A password submission must never replay its body during authentication refresh.
    retryAuth: false,
  })
}

export function isMfaRequired(
  response: LoginResponse['data'],
): response is components['schemas']['AuthMFARequired'] {
  return 'sessionId' in response
}

export function isLoginSuccess(
  response: LoginResponse['data'],
): response is components['schemas']['AuthLoginSuccess'] {
  return 'accessToken' in response
}
